import axios from 'axios'

// Use VITE_BACKEND_URL for Vite (instead of REACT_APP_BACKEND_URL)
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // 10 minutes default
  maxContentLength: 100 * 1024 * 1024, // 100MB - maximum size of the response
  maxBodyLength: 100 * 1024 * 1024, // 100MB - maximum size of the request body
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dizeeToken')
    if (token) {
      try {
        const parsedToken = JSON.parse(token)
        config.headers.Authorization = `Bearer ${parsedToken}`
      } catch (e) {
        // If token is not JSON, use as-is
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    
    // Log the actual timeout being used
    console.log('🔧 Axios request interceptor - Final config:', {
      url: config.url,
      method: config.method,
      timeout: config.timeout,
      baseURL: config.baseURL
    })
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Helper function to check if user is in team member context
const isTeamMemberContext = (): boolean => {
  const selectedArtist = localStorage.getItem('selectedArtist')
  return !!selectedArtist
}

// Helper function to get the effective user ID
const getEffectiveUserId = (): string | null => {
  const selectedArtist = localStorage.getItem('selectedArtist')
  if (selectedArtist) {
    try {
      const artist = JSON.parse(selectedArtist)
      return artist._id || null
    } catch (error) {
      console.error('Error parsing selectedArtist:', error)
    }
  }
  return null
}

// Endpoints that are "current user" scoped (e.g. "my teams", "my invitations").
// They must NOT receive the selected artist's userId, or the backend may return
// the wrong or empty list (e.g. artists that the selected artist manages instead
// of artists this team member can access).
const USER_SCOPED_ENDPOINTS = [
  'team/managed-artists',
  'team/invitation-requests',
  'team/members',
]

const shouldAttachUserId = (endpoint: string): boolean => {
  return !USER_SCOPED_ENDPOINTS.includes(endpoint)
}

export const getDataAPI = async (endpoint: string, token?: string) => {
  try {
    const config: any = {}
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    if (effectiveUserId && shouldAttachUserId(endpoint)) {
      config.params = { userId: effectiveUserId }
    }

    const response = await api.get(endpoint, config)
    return response.data
  } catch (error) {
    console.error('GET API Error:', error)
    throw error
  }
}

export const postDataAPI = async (endpoint: string, data: any, token?: string, config?: any) => {
  try {
    const requestConfig: any = {
      maxContentLength: 100 * 1024 * 1024, // 100MB
      maxBodyLength: 100 * 1024 * 1024, // 100MB
      ...config // Spread options like timeout
    }
    
    if (token) {
      requestConfig.headers = { ...requestConfig.headers, Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    if (effectiveUserId && shouldAttachUserId(endpoint)) {
      data = { ...data, userId: effectiveUserId }
    }

    console.log('🔧 Request config:', {
      endpoint,
      timeout: requestConfig.timeout || 'default (10min)',
      maxContentLength: requestConfig.maxContentLength,
      maxBodyLength: requestConfig.maxBodyLength,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : []
    })

    const response = await api.post(endpoint, data, requestConfig)
    return response.data
  } catch (error) {
    console.error('POST API Error:', error)
    throw error
  }
}

export const putDataAPI = async (endpoint: string, data: any, token?: string) => {
  try {
    const config: any = {}
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    if (effectiveUserId && shouldAttachUserId(endpoint)) {
      data = { ...data, userId: effectiveUserId }
    }

    const response = await api.put(endpoint, data, config)
    return response.data
  } catch (error) {
    console.error('PUT API Error:', error)
    throw error
  }
}

export const deleteDataAPI = async (endpoint: string, token?: string) => {
  try {
    const config: any = {}
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    if (effectiveUserId && shouldAttachUserId(endpoint)) {
      config.params = { userId: effectiveUserId }
    }

    const response = await api.delete(endpoint, config)
    return response.data
  } catch (error) {
    console.error('DELETE API Error:', error)
    throw error
  }
}

/**
 * Clears selectedArtist and all caches that are scoped to the current "acting as"
 * artist (team member context). Call this when the user was removed from a team
 * so they no longer see that artist's data.
 */
export const clearStaleSelectedArtistAndCaches = () => {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (
      key &&
      (key.startsWith('showsPageCache') ||
        key.startsWith('musicPageCatalogCache') ||
        key.startsWith('showsTicketTrackingOverrides') ||
        key.startsWith('pendingBandsintownShows') ||
        key.startsWith('teamMembersCache') ||
        key.startsWith('teamHubCache') ||
        key.startsWith('dizee:granularPerms'))
    ) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k))
  localStorage.removeItem('selectedArtist')
  sessionStorage.clear()
}

export { api, isTeamMemberContext, getEffectiveUserId }
